import { useQuery } from '@apollo/client/react';
import { Box, Center, Flex, Spinner } from '@chakra-ui/react';
import { ConversionGate } from '@src/components/sco/Conversion/controller/ConversionGate';
import { ArticleDetailHero } from '@src/components/sco/Education/ArticleDetail/ArticleDetailHero';
import { useEducationContext } from '@src/components/sco/Education/EducationContext';
import { EducationErrorState } from '@src/components/sco/Education/EducationErrorState';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { BoxedCardCTA } from '@src/components/sco/UserAccessGate/cta-variants/BoxedCardCTA';
import { ViewableTracker } from '@src/components/sco/ViewableTracker';
import { ArticleSEO } from '@src/components/sco/utilities/SEOProvider/ContentItemSEO/ArticleSEO';
import { BreadcrumbItem } from '@src/contexts/ActiveContentContext';
import { Article } from '@src/graphql-types.ts';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect';
import { gql } from '@src/lib/gql';
import { useParams } from 'next/navigation';
import { useQueryState } from 'nuqs';
import React from 'react';
type ArticleDetailProps = {
  articleSlug: string;
  typeTitle?: string;
  typeSlug?: string;
  getBreadcrumbs?: (article: Article) => BreadcrumbItem[];
};
export const ArticleDetail = (props: ArticleDetailProps) => {
  const {
    currentUser
  } = useCurrentUser();
  const [prompt, setPrompt] = useQueryState('prompt');
  const routeParams = useParams();
  const educationContext = useEducationContext();
  const articleSlug = routeParams?.articleSlug || props.articleSlug;
  const articleQuery = useQuery(gql`
      query ($slug: String!) {
        article(where: { slug: $slug }) {
          id
          title
          seoDescription
          slug
          terms {
            id
            name
            slug
            isPrimary
          }
          featuredImage {
            id
            resizedUrl: url(transformation: { image: { resize: { fit: max, width: 1000 } } })
            altText
            height
            width
          }
          body
          authors {
            id
            firstName
            lastName
            slug
            avatar {
              id
              url
            }
          }
          subjectNames
          subjectLogos {
            id
            url
          }
          ${ArticleSEO.fragments.Article()}
        }
      }
    `, {
    variables: {
      slug: articleSlug
    }
  });
  const article = articleQuery.data?.article;
  useUniversalEffect(() => {
    if (article) {
      educationContext.setActiveContentItem(article);
      if (props.getBreadcrumbs) {
        educationContext.setBreadcrumbs(props.getBreadcrumbs(article));
      } else {
        educationContext.setBreadcrumbs([{
          title: props.typeTitle || 'Articles',
          href: `/${props.typeSlug || 'articles'}`
        }, {
          title: `${article?.title || '...'}`,
          href: `/${props.typeSlug || 'articles'}/${articleSlug}`
        }]);
      }
    }
  }, [articleSlug, article?.id, article?.title]);
  return <Flex align={'center'} direction={'column'} justify={'stretch'} width={'full'}>
      {articleQuery.loading && <Center padding={10}>
          <Spinner />
        </Center>}
      {articleQuery.error && <p>
          {'Error: '}
          {articleQuery.error.message}
        </p>}
      {!articleQuery.loading && !article && <EducationErrorState ctaHref={'/articles'} ctaLabel={'Explore All Articles'} headingText={'Article not found.'} />}
      {article && <>
          <ViewableTracker trackScrolling={true} viewableId={article.id} viewableType={article.__typename}>
            {/** HERO */}
            <ArticleDetailHero authors={article.authors} authorSlug={article.authors[0]?.slug} avatar={article.authors[0]?.avatar?.url} featuredImage={(article.featuredImage as any)?.resizedUrl} firstName={article.authors[0]?.firstName} lastName={article.authors[0]?.lastName} slug={article.slug} subjectLogos={article.subjectLogos[0]?.url} subjectNames={article.subjectNames.join(' & ')} title={article.title} typeSlug={props.typeSlug} />
            {/** BODY */}
            <Box maxW={'container.md'} paddingX={6}>
              <MarkdownRenderer baseFontSize={'16px'} baseHeadingColor={'textPrimary'} baseTextColor={'textPrimaryAlpha.900'} preset={'article'} stripBlockquoteSingleBreaks={true} stripWikiLinkBrackets={true}>
                {article.body}
              </MarkdownRenderer>
            </Box>
          </ViewableTracker>
          <Box marginBottom={'10em'} maxWidth={'container.md'} padding={'1.5em'} width={'full'}>
            {currentUser?.isGuest && <BoxedCardCTA boxProps={{
          bgColor: 'actionActiveSecondary'
        }} title={`Find this article helpful?`} emailCaptureProps={{
          afterSuccess: () => {
            if (currentUser?.isPasswordSet) {
              setPrompt('welcome');
            } else {
              setPrompt('register');
            }
          },
          inputProps: {
            colorScheme: 'whiteAlpha'
          }
        }} subtitle={<>
                    This is just a small sample! Register to unlock our in-depth courses, hundreds of video
                    courses, and a library of playbooks and articles to grow your startup fast. Let us show
                    you!
                  </>} />}

            {currentUser?.isRegistered && !currentUser?.contentPermissions?.canAccessCourses && <Box bgColor={'actionActiveSecondary'} borderRadius={'lg'} width={'full'}>
                <ConversionGate density={'boxed'} headline={`Find this article helpful? There's so much more!`} intent={'content-lock'} permission={'canAccessCourses'} />
              </Box>}
          </Box>
        </>}
    </Flex>;
};